import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import { Stack, Typography } from "@mui/material";
import { ApplicationSummary, ClientConfig, ClientsService } from "gen/clients";
import { Form, Formik } from "formik";
import { SelectOneField } from "components/formikFields/SelectOneField/SelectOneField";
import { LoadingButton } from "../../../../../../components/LoadingButton/LoadingButton";
import Button from "@mui/material/Button";
import { useMutation } from "react-query";
import { ApiErrorMessage } from "../../../../../../components/ApiErrorMessage/ApiErrorMessage";

interface Props {
  clientConfig: ClientConfig;
  availableToAddApplications: ApplicationSummary[];
  onClose: () => void;
  onSuccess: () => void;
}

enum FieldName {
  APP = "app"
}

interface FormValues {
  [FieldName.APP]: string;
}

const AddApplicationDialog: React.FC<Props> = ({ clientConfig, availableToAddApplications, onClose, onSuccess }) => {
  const { mutateAsync, error } = useMutation(ClientsService.updateClientConfig);

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      const enabledApplications = [...(clientConfig.enabledApplications || [])];
      enabledApplications.push(values[FieldName.APP]);
      await mutateAsync(
        {
          id: clientConfig.id,
          requestBody: {
            ...clientConfig,
            enabledApplications
          }
        },
        { onSuccess }
      );
    },
    [clientConfig, mutateAsync, onSuccess]
  );

  const options = React.useMemo(
    () => availableToAddApplications.map(a => ({ label: a.name, value: a.id })),
    [availableToAddApplications]
  );

  return (
    <Dialog open={true} fullWidth={true} maxWidth="xs" onClose={onClose}>
      <DialogTitle>Add Application</DialogTitle>
      <DialogContent>
        <Formik initialValues={{ [FieldName.APP]: "" }} onSubmit={onSubmit}>
          {({ isSubmitting }) => (
            <Form noValidate={true} autoComplete="off" autoCorrect="off">
              <Stack spacing={2}>
                <Typography>Select application that you would like to add:</Typography>
                <SelectOneField name={FieldName.APP} options={options} required={true} variant="outlined" />
                {error && <ApiErrorMessage apiError={error} />}
                <Stack pt={3} spacing={1} direction="row" justifyContent="center">
                  <LoadingButton isLoading={isSubmitting}>Add Application</LoadingButton>
                  <Button onClick={onClose} disabled={isSubmitting}>
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export { AddApplicationDialog };
